import { PageHeader } from "$root/components/PageHeader";
import { StackSidePanelContext, StackSidePanelContextProvider } from "$root/components/common/Stack/StackSidePanel";
import {
	ExposureGrid,
	PerformanceChart,
	PortfolioMetrics,
} from "$root/functional-areas/portfolio-lab/components/charts";
import { CompositionEditor } from "$root/functional-areas/portfolio-lab/components/composition-editor";
import {
	PortfolioLabContext,
	PortfolioLabContextProvider,
	pageSplitToCssWidth,
	portfolioLabAdapter,
	scenarioIdFromColumns,
	type PageSplitRatio,
	type PortfolioLabInvestmentItem,
	type PortfolioLabProps,
} from "$root/functional-areas/portfolio-lab/ctx";
import { PortfolioLabSettings, PortfolioLabWidgetToolbar } from "$root/functional-areas/portfolio-lab/components";
import { COMPOSITION_GROUP_BY_UNGROUP, DEFAULT_COMPOSITION_GROUP_BY } from "$root/functional-areas/portfolio-lab/state";
import { userClassificationsMap$ } from "$root/functional-areas/instruments/user-classifications";
import { ReactQueriesWrapperBase } from "$root/components/ReactQueryWrapper";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import { CircularProgressBar, Column } from "@mdotm/shadcn/legacy/components";
import { readAtom, useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { ForEach, useMandatoryContext } from "@mdotm/utils/react-extra";
import { portfolioLabRepository } from "$root/functional-areas/portfolio-lab/repository";
import {
	chartsStale,
	computeHoldsUpdateCharts,
	LAB_COMPUTE_RETRY_MS,
} from "$root/functional-areas/portfolio-lab/common";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useInvestmentLab } from "$root/functional-areas/portfolio-lab/hooks/useInvestmentLab";
import { useLabSession } from "$root/functional-areas/portfolio-lab/hooks/useLabSession";
import { useBotWriteResync } from "$root/functional-areas/portfolio-lab/hooks/useBotWriteResync";
import { useRecomputeMetricsOnLoad } from "$root/functional-areas/portfolio-lab/hooks/useRecomputeMetricsOnLoad";
import { useLabComputeEvents } from "$root/functional-areas/portfolio-lab/hooks/useLabComputeEvents";
import styles from "./PortfolioLab.module.css";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useFunctionalAreas } from "$root/App/context";

/**
 * A column's source type, as the lab tools name it (`VirtualInvestmentSourceType`), from the lab's
 * own item type. Only for the assistant's context line, so it can say what a column is a copy OF.
 *
 * A duplicated column cannot be reported as such: the adapter maps the API's `VIRTUAL` source onto
 * `INVESTMENT` (`sourceTypeToLabType`'s fallback), so by the time it reaches the page the fact that
 * it is a copy of another column is gone. The tools report it correctly when asked.
 */
const LAB_TYPE_TO_SOURCE_TYPE: Record<PortfolioLabInvestmentItem["type"], string> = {
	INVESTMENT: "PORTFOLIO",
	INVESTMENT_PROPOSAL: "INVESTMENT_PROPOSAL",
	BENCHMARK: "BENCHMARK",
	REFERENCE: "REFERENCE",
};

function PortfolioLabInner() {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const investments = useAtomValue(ctx.investments$);
	const pageSplit = useAtomValue(ctx.pageSplit$);
	const charts = useAtomValue(ctx.charts$);
	const investmentLab = useInvestmentLab();
	const { saveLabSession } = useLabSession();

	// The restored charts read server-side reports that only `/compute` writes, so a composition
	// edited without clicking "Update Charts" comes back paired with the previous composition's
	// numbers. Recompute those columns once on load so what's on screen matches the composition.
	useRecomputeMetricsOnLoad();

	// `/compute` only queues a column's compute; its outcome arrives as a `lab-compute` event.
	useLabComputeEvents();

	// SphereAI can edit this session through the `portfolio_lab-*` MCP tools. The columns are seeded
	// into atoms once per mount, so a bot write would otherwise be invisible here — and the next local
	// edit would autosave over it. See useBotWriteResync.
	useBotWriteResync();

	// Expose the open lab to SphereAI so "this column" / "the lab" resolve to what the user is
	// actually looking at, and so it addresses a column by the COLUMN uuid rather than the uuid of the
	// portfolio the column was copied from. Unlike every other screen's context this is a LIST — the
	// lab holds several columns at once — so all of them are named; see the lab descriptor in
	// sphere-ai/appContext. The dependency is the derived tuple, not `investments`, so a weight edit
	// (which replaces those items on every keystroke) does not re-dispatch the area.
	const selectedScenarioId = useAtomValue(ctx.selectedScenarioId$);
	const labColumns = investments.map((investment) => ({
		columnUuid: investment.virtualUuid,
		name: investment.name,
		sourceType: LAB_TYPE_TO_SOURCE_TYPE[investment.type],
	}));
	// The columns' CONTENT, as a primitive — the honest trigger for re-registering the area, and the
	// house pattern for it (every other page's context deps are primitives too). Depending on
	// `labColumns` instead would re-dispatch on every keystroke of a weight edit, since the atom
	// replaces its items each time and the identity of the mapped array always changes with it.
	const labColumnsKey = labColumns.map((column) => `${column.columnUuid}${column.name}`).join("");
	// The editor's group-by, so "the groups" and "the largest positions" resolve to the rows on screen.
	// Primitives again: the level, the classification, and its name (which loads after the uuid).
	const groupBy = useAtomValue(ctx.compositionGroupBy$);
	const classificationsMap = useAtomValue(userClassificationsMap$);
	const groupByClassificationUuid =
		groupBy.secondLevel === COMPOSITION_GROUP_BY_UNGROUP ? undefined : groupBy.secondLevel;
	const groupByClassificationName = groupByClassificationUuid
		? classificationsMap?.get(groupByClassificationUuid)?.name
		: undefined;
	// biome-ignore lint/correctness/useExhaustiveDependencies: `labColumnsKey` is the trigger; the columns are read live inside the provider, which runs at dispatch time.
	useFunctionalAreas(
		() => ({
			areas: "portfolioLab",
			data: {
				columns: labColumns,
				scenarioId: selectedScenarioId,
				groupBy: {
					firstLevel: groupBy.firstLevel,
					classificationUuid: groupByClassificationUuid,
					classificationName: groupByClassificationName,
				},
			},
		}),
		[labColumnsKey, selectedScenarioId, groupBy.firstLevel, groupByClassificationUuid, groupByClassificationName],
	);

	const onReloadMetrics = useCallback(async () => {
		const investments = readAtom(ctx.investments$);
		// `onlyStale` recomputes only stale columns; report that count alongside the total so we can
		// see how much work an "Update Charts" click actually triggers.
		trackMixPanelEvent("portfolioLab.updateCharts", {
			updatedColumns: investments.filter(chartsStale).length,
			columns: investments.length,
		});
		await investmentLab.reloadInvestmentsMetrics(investments, { awaitCompletion: true, onlyStale: true });
	}, [ctx.investments$, investmentLab.reloadInvestmentsMetrics]);

	const onPageSplitChange = useCallback(
		(next: PageSplitRatio) => {
			// Write the client-authoritative atom, then trigger the shared last-write-wins session save,
			// which reads the freshly written `pageSplit$` (and every other view-config atom) live.
			writeAtom(ctx.pageSplit$, { kind: "ratio", width: next });
			saveLabSession();
		},
		[ctx.pageSplit$, saveLabSession],
	);

	// A stale column whose compute is queued lights the button only once that compute has waited
	// LAB_COMPUTE_RETRY_MS with no result, since a click restarts it; the timer redraws at that moment.
	const [now, setNow] = useState(Date.now);
	const heldSince = investments.flatMap((i) =>
		chartsStale(i) && computeHoldsUpdateCharts(i, now) && i.pendingCompute ? [i.pendingCompute.queuedAt] : [],
	);
	const nextRelease = heldSince.length > 0 ? Math.min(...heldSince) + LAB_COMPUTE_RETRY_MS : null;
	useEffect(() => {
		if (nextRelease == null) {
			return;
		}
		const timer = setTimeout(() => setNow(Date.now()), nextRelease - Date.now());
		return () => clearTimeout(timer);
	}, [nextRelease]);
	const needToReloadChart = investments.some((i) => chartsStale(i) && !computeHoldsUpdateCharts(i, now));

	return (
		<>
			<PageHeader crumbs={[{ label: "Portfolio Studio" }, { label: "Portfolio Lab" }]} />
			<div className={styles.host}>
				<div
					data-qualifier="PortfolioLab/Shell"
					className={styles.shell}
					style={{ "--left-width": pageSplitToCssWidth(pageSplit) } as React.CSSProperties}
				>
					<div data-peek-side="left" data-lab-area="left" className="overflow-hidden flex flex-col">
						<CompositionEditor />
					</div>
					<PortfolioLabWidgetToolbar
						data-peek-side="right"
						data-lab-area="toolbar"
						needToReloadChart={needToReloadChart}
						onReloadMetrics={onReloadMetrics}
						pageSplit={pageSplit}
						onPageSplitChange={onPageSplitChange}
						onOpenSettings={() => sidePanelCtx.setOpen(true)}
					/>
					<Column
						minSize="0"
						data-peek-side="right"
						data-lab-area="charts"
						gap={12}
						className="overflow-y-auto"
						childrenShrink={0}
					>
						<ForEach collection={charts} keyProvider={(c) => c.uuid}>
							{(chartConfig) => {
								if (!chartConfig.visible) {
									return null;
								}
								switch (chartConfig.type) {
									case "PERFORMANCE":
										return <PerformanceChart chartConfig={chartConfig} />;
									case "EXPOSURE":
										return <ExposureGrid chartConfig={chartConfig} />;
									case "TABLE":
										return <PortfolioMetrics chartConfig={chartConfig} />;
									default:
										return null;
								}
							}}
						</ForEach>
					</Column>
				</div>
			</div>
		</>
	);
}

function PortfolioLabLoaded({
	initialInvestments,
	initialLabSession,
	initialScenarioId,
}: {
	initialInvestments: PortfolioLabInvestmentItem[];
	initialLabSession: PortfolioLabProps;
	initialScenarioId: string | null;
}) {
	const { investments: _investments, ...labSession } = initialLabSession;
	return (
		<PortfolioLabContextProvider
			investments={initialInvestments}
			selectedScenarioId={initialScenarioId}
			{...(labSession ?? {
				charts: [],
				parametrizedConcepts: [],
				pageSplit: { kind: "ratio", width: "1/2" },
				decimals: 2,
				grid: { groupBy: DEFAULT_COMPOSITION_GROUP_BY, columns: [] },
			})}
		>
			<StackSidePanelContextProvider root={PortfolioLabSettings}>
				<PortfolioLabInner />
			</StackSidePanelContextProvider>
		</PortfolioLabContextProvider>
	);
}

export default function PortfolioLab() {
	// The provider seeds its atoms once, so it must seed from data fetched for THIS mount, never from a
	// cached snapshot: a write that landed while the lab was not mounted (a SphereAI tool call made from
	// another page, another tab) only marked the cache stale, and a seed from it would never be
	// corrected — the refetch that follows lands in the cache and goes nowhere. So both bootstrap
	// queries always refetch on mount, and the page waits for that fetch (or its failure) below.
	const virtualInvestmentsQuery = useQuery({
		...portfolioLabRepository.listVirtualInvestments.getQueryConfig(),
		refetchOnMount: "always",
	});
	const labSessionQuery = useQuery({
		...portfolioLabRepository.getLabSession.getQueryConfig(),
		refetchOnMount: "always",
	});

	const mappedQuery = useMappedQueries(
		([upstreamVirtualInvestments, upstreamLabSession]) => ({
			initialInvestments: portfolioLabAdapter.virtualInvestment.toContext(upstreamVirtualInvestments),
			initialLabSession: portfolioLabAdapter.labSession.toContext(upstreamLabSession),
			initialScenarioId: scenarioIdFromColumns(upstreamVirtualInvestments),
		}),
		[],
		[virtualInvestmentsQuery, labSessionQuery],
	);

	const loadingFallback = <CircularProgressBar value="indeterminate" className="m-auto" />;

	if (!virtualInvestmentsQuery.isFetchedAfterMount || !labSessionQuery.isFetchedAfterMount) {
		return loadingFallback;
	}

	return (
		<ReactQueriesWrapperBase queries={[mappedQuery]} loadingFallback={loadingFallback}>
			{([{ initialInvestments, initialLabSession, initialScenarioId }]) => (
				<PortfolioLabLoaded
					initialInvestments={initialInvestments}
					initialLabSession={initialLabSession}
					initialScenarioId={initialScenarioId}
				/>
			)}
		</ReactQueriesWrapperBase>
	);
}
